import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AddAttachmentComponent } from './add-attachment/add-attachment.component';
import { DrawerService } from '../../../../core/services/drawer.service';
import { StudentAttachmentService } from './student-attachment.service';
import {
  filter,
  finalize,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import {
  Attachment,
  AttachmentsByPlanTypeFolder,
  CaseDetailModel,
  PlanTypeFolder,
  ManageStudentDrawerParams,
  SelectedFolder,
} from '../../insights.interface';
import { AttachmentMode, InsightMode } from '../../insights.enums';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { LoaderService } from '../../../../core/services/loader.service';
import { CommonService } from '../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../core/models/error.models';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzFormModule } from 'ng-zorro-antd/form';
import { AttachmentFolderService } from './attachment-folder.service';
import { ApiResponse } from '../../../../core/models/api-response.models';
import { NzIconModule } from 'ng-zorro-antd/icon';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import {
  HELP_TEXT_TRUNCATE_LIMIT,
  TruncatePipe,
} from '../../../../shared/pipes/truncate.pipe';
import { QuestionnairesFormComponent } from './questionnaires-form/questionnaires-form.component';
import { QuestionnaireMode } from './questionnaires-form/questionnaires.interface';
import { AuditLogsService } from '../../../audit-logs/audit-logs.service';
import {
  AuditModule,
  AuditOperationType,
} from '../../../audit-logs/audit-logs.constants';
import { AuditContextService } from '../../../audit-logs/audit-context.service';
import { DrawerHeaderComponent } from '../../../../components/drawer-header/drawer-header.component';
import { HelpTextComponent } from '../../../../components/help-text/help-text.component';
import { BipTemplateGatingService } from '../../../../shared/services/bip-template-gating.service';
import { TemplateModel } from '../../../bip-template/bip-template.interface';

interface AttachmentWithSelection {
  planTypeId: number;
  title: string;
  folders: SelectedFolder[];
  isRequired: boolean;
  isHardStop: boolean;
  helpText: string | null;
}

@Component({
  selector: 'app-insights-admin',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzButtonModule,
    CommonModule,
    NzSpinModule,
    NzEmptyModule,
    ConfirmDialogComponent,
    NzCheckboxModule,
    FormsModule,
    NzCheckboxModule,
    NzInputModule,
    NzIconModule,
    NzFormModule,
    FormsModule,
    NzCollapseModule,
    ReactiveFormsModule,
    DrawerHeaderComponent,
    NzToolTipModule,
    TruncatePipe,
    HelpTextComponent,
  ],
  templateUrl: './insights-admin.component.html',
  styleUrl: './insights-admin.component.scss',
})
export class InsightsAdminComponent implements OnInit, OnDestroy {
  private readonly studentAttachmentService = inject(StudentAttachmentService);
  private readonly loaderService = inject(LoaderService);
  private readonly auditContextService = inject(AuditContextService);
  private readonly auditLogsService = inject(AuditLogsService);

  steps: AttachmentWithSelection[] = [];
  currentIndex = 0;
  private hasResolvedInitialStep = false;
  folders: SelectedFolder[] = [];
  hasAttachments = false;

  private readonly destroy$ = new Subject<void>();

  @ViewChild('addAttachmentDrawerTitleTpl')
  addAttachmentDrawerTitleTpl?: TemplateRef<object>;

  @ViewChild('questionnairesDrawerTitleTpl')
  questionnairesDrawerTitleTpl?: TemplateRef<object>;

  loading$: Observable<boolean> = of(false);

  caseParams: ManageStudentDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.VIEW,
  };

  readonly atttachmentMode = AttachmentMode;
  readonly questionnaireMode = QuestionnaireMode;
  showConfirmDialog = false;
  isDeleting = false;
  attachmentId: null | number = null;
  confrimDialogMsg = 'Are you sure you want to delete this attachment?';
  isAddFolderOpen = false;
  selectAllByStep: Record<number, boolean> = {};
  folderForm!: FormGroup;
  selectedPanel: boolean[] = [];
  titlePattern = /^\S(.*\S)?$/;
  isSavingFolder = false;
  bipTemplate: TemplateModel | null = null;
  constructor(
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly folderSrv: AttachmentFolderService,
    private readonly fb: FormBuilder,
    private readonly bipTemplateGatingService: BipTemplateGatingService
  ) {
    this.setupDrawerSubscription();
    this.loading$ = this.loaderService.loading$;
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.resetComponentState();
    this.loadPlansWithAttachments();
  }

  getQuestionnaireSectionHelpText(): string | null {
    return this.bipTemplateGatingService.getSectionHelpText(
      this.bipTemplate,
      'Questionnaires'
    );
  }

  loadPlansWithAttachments(): void {
    this.loaderService.show();
    const caseId = this.caseParams.case?.id;
    if (!caseId) {
      this.loaderService.hide();
      return;
    }
    this.studentAttachmentService
      // .getAttachmentsWithPlanTypeId(caseId)
      .getAttachmentsByPlanTypeFolder(caseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const attachmentGroups = response.result.data || [];
          const previousStepPlanTypeId = this.currentStep?.planTypeId;

          this.steps = this.buildSteps(attachmentGroups);
          this.resetComponentState();
          this.currentIndex = this.resolveCurrentIndex(
            previousStepPlanTypeId,
            response.result.draftStateQuestionnairePlanTypeId
          );
          this.loaderService.hide();
        },

        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.resetComponentState();
          this.loaderService.hide();
        },
      });
  }

  // General Attachments (planTypeId 0) is always first — the API already
  // returns it with sequenceNumber 0, but sort defensively in case that
  // ever changes.
  private buildSteps(
    attachmentGroups: AttachmentsByPlanTypeFolder[]
  ): AttachmentWithSelection[] {
    return [...attachmentGroups]
      .sort((a, b) => {
        if (a.planTypeId === 0 || b.planTypeId === 0) {
          return a.planTypeId - b.planTypeId;
        }
        return a.sequenceNumber - b.sequenceNumber;
      })
      .map(group => ({
        planTypeId: group.planTypeId,
        title: group.planTypeName,
        isRequired: group.isRequired,
        isHardStop: group.isHardStop,
        helpText: group.helpText,
        // folders: this.groupByFolder(
        //   group.attachments.map(att => this.mapAttachment(att))
        // ),
        folders: this.mapFolders(group.folders ?? []),
      }));
  }

  // The API returns every case folder under every plan (empty ones included)
  // so users can always find a folder to upload into.
  private mapFolders(folders: PlanTypeFolder[]): SelectedFolder[] {
    return folders.map(folder => ({
      folderId: folder.folderId,
      folderName: folder.folderName,
      selected: false,
      active: false,
      attachments: (folder.attachments ?? []).map(att =>
        this.mapAttachment(att)
      ),
    }));
  }

  // On first load, resume at the section the backend flagged via
  // draftStateQuestionnairePlanTypeId (matched against planTypeId); on a
  // reload triggered by an in-drawer action (add attachment/questionnaire/
  // delete), stay on the step the user was already viewing instead of
  // jumping back to that draft section.
  private resolveCurrentIndex(
    previousStepPlanTypeId: number | undefined,
    draftStateQuestionnairePlanTypeId: number | null | undefined
  ): number {
    if (this.hasResolvedInitialStep) {
      const stayIndex = this.steps.findIndex(
        step => step.planTypeId === previousStepPlanTypeId
      );
      return Math.max(stayIndex, 0);
    }

    this.hasResolvedInitialStep = true;
    const resumeIndex = this.steps.findIndex(
      step => step.planTypeId === draftStateQuestionnairePlanTypeId
    );
    return Math.max(resumeIndex, 0);
  }

  get currentStep(): AttachmentWithSelection | null {
    return this.steps[this.currentIndex] ?? null;
  }

  get isFirstStep(): boolean {
    return this.currentIndex === 0;
  }

  get isLastStep(): boolean {
    return this.currentIndex === this.steps.length - 1;
  }

  stepAttachmentCount(step: AttachmentWithSelection | null): number {
    return (
      step?.folders?.reduce(
        (count, folder) => count + folder.attachments.length,
        0
      ) ?? 0
    );
  }

  get canProceed(): boolean {
    if (!this.currentStep) {
      return false;
    }
    return (
      !this.currentStep.isHardStop ||
      this.stepAttachmentCount(this.currentStep) > 0
    );
  }

  // Gates the Finish button on the last step — unlike canProceed (current
  // step only), this checks every Required-or-Hard-Stop step across the
  // whole wizard, since Finish means the entire case's attachments are done.
  get canFinish(): boolean {
    return !this.steps.some(
      step =>
        (step.isRequired || step.isHardStop) &&
        this.stepAttachmentCount(step) === 0
    );
  }

  isHelpTextTruncated(text: string | null | undefined): boolean {
    return (text ?? '').trim().length > HELP_TEXT_TRUNCATE_LIMIT;
  }

  // Sent on every attachment/questionnaire-PDF upload so the backend can
  // track resume position: draftStateQuestionnairePlanTypeId is the section
  // this upload belongs to, and isDraftQuestionnaire flips to false (with
  // draftStateQuestionnairePlanTypeId null) only once every Required-or-
  // Hard-Stop section elsewhere in the case already has at least one
  // attachment — this upload's own section is excluded from that check
  // since it's the one about to be satisfied.
  private computeIsDraftQuestionnaire(planTypeId: number): {
    isDraftQuestionnaire: boolean;
    draftStateQuestionnairePlanTypeId: number | null;
  } {
    if (this.steps.length === 0) {
      return {
        isDraftQuestionnaire: true,
        draftStateQuestionnairePlanTypeId: planTypeId,
      };
    }
    const stillOutstanding = this.steps.some(
      step =>
        step.planTypeId !== planTypeId &&
        (step.isRequired || step.isHardStop) &&
        this.stepAttachmentCount(step) === 0
    );
    return stillOutstanding
      ? {
          isDraftQuestionnaire: true,
          draftStateQuestionnairePlanTypeId: planTypeId,
        }
      : {
          isDraftQuestionnaire: false,
          draftStateQuestionnairePlanTypeId: null,
        };
  }

  onNext(): void {
    if (!this.canProceed || this.isLastStep) {
      return;
    }
    this.currentIndex++;
  }

  onPrevious(): void {
    if (this.isFirstStep) {
      return;
    }
    this.currentIndex--;
  }

  // Every Required-or-Hard-Stop plan must have at least one attachment before
  // Finish is allowed to clear the case's draft state; otherwise surface the
  // same pending-items messaging as the wizard-hard-stop banner and stop.
  onFinish(): void {
    if (!this.canFinish) {
      this.commonService.notify(
        'error',
        'At least one attachment is required for every Required and Hard Stop plan to finish.'
      );
      return;
    }
    const caseId = this.caseParams.case?.id;
    if (!caseId) {
      return;
    }
    this.loaderService.show();
    this.studentAttachmentService
      .finalizeDraftState(caseId)
      .pipe(
        finalize(() => this.loaderService.hide()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.drawerService.closeDrawer(DRAWER_IDS.BIP_STUDENT_ATTACHMENT);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private mapAttachment(att: Attachment): Attachment {
    return {
      ...att,
      selected: false,
      folderId: att.folderId ?? 1,
      folderName: att.folderName || 'Default',
    } as Attachment;
  }

  groupByFolder(attachments: Attachment[]): SelectedFolder[] {
    const grouped = groupBy(attachments, 'folderId');
    return map(grouped, (atts, folderId) => ({
      folderId: Number(folderId),
      folderName: atts[0].folderName || '',
      selected: false,
      active: true,
      attachments: atts,
    }));
  }

  onFolderExpand(folders: SelectedFolder[], index: number, isActive: boolean) {
    folders[index].active = isActive;
  }

  selectedCount(folders: SelectedFolder[]): number {
    return (
      folders.reduce((count, folder) => {
        const selectedInFolder =
          folder.attachments?.filter((att: Attachment) => att.selected)
            ?.length || 0;
        return count + selectedInFolder;
      }, 0) || 0
    );
  }

  onSelectAllChange(folders: SelectedFolder[], planTypeId: number): void {
    const selectAllValue = this.selectAllByStep[planTypeId];
    this.foldersWithAttachments(folders).forEach(folder => {
      folder.selected = selectAllValue;
      folder.attachments.forEach(
        (att: Attachment) => (att.selected = selectAllValue)
      );
    });
  }

  onSelectFolderChange(
    folders: SelectedFolder[],
    folderId: number,
    planTypeId?: number
  ): void {
    const folder = folders.find(f => f.folderId === folderId);
    if (!folder) return;

    folder.attachments.forEach(
      att => (att.selected = folder.selected || false)
    );
    this.updateSelectAllState(folders, planTypeId);
  }

  onAttachmentSelectionChange(
    folders: SelectedFolder[],
    planTypeId?: number
  ): void {
    this.foldersWithAttachments(folders).forEach(folder => {
      folder.selected = folder.attachments.every(att => att.selected);
    });
    this.updateSelectAllState(folders, planTypeId);
  }

  // Empty folders are rendered but can't be selected, so they're excluded
  // from every select/select-all calculation.
  private foldersWithAttachments(folders: SelectedFolder[]): SelectedFolder[] {
    return folders.filter(folder => folder.attachments.length > 0);
  }

  updateSelectAllState(folders: SelectedFolder[], planTypeId?: number): void {
    if (planTypeId === undefined) {
      return;
    }
    const selectableFolders = this.foldersWithAttachments(folders);
    const allSelected =
      selectableFolders.length > 0 &&
      selectableFolders.every(
        folder =>
          folder.selected && folder.attachments.every(att => att.selected)
      );
    this.selectAllByStep[planTypeId] = allSelected;
  }

  private resetSelectionState(): void {
    this.selectAllByStep = {};

    this.steps.forEach(step => {
      this.selectAllByStep[step.planTypeId] = false;
      step.folders?.forEach(folder => {
        folder.selected = false;
        folder.attachments.forEach(att => (att.selected = false));
      });
    });
  }

  private resetComponentState(): void {
    this.resetSelectionState();
    this.showConfirmDialog = false;
    this.isDeleting = false;
    this.attachmentId = null;
  }

  private logDownloadAction(
    action: 'selected' | 'all',
    item: AttachmentWithSelection
  ): void {
    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;
    const logMessage =
      action === 'selected'
        ? `Downloaded selected attachments for ${studentName}_${item.title}`
        : `Downloaded all attachments for ${studentName}_${item.title}`;

    this.auditLogsService.logAudit({
      auditModule: AuditModule.CaseAttachment,
      auditOperationType: AuditOperationType.Downloaded,
      summary: logMessage,
      organizationIdOverride: this.caseParams.case?.student?.organizationId,
    });
  }

  private downloadAttachments(
    attachments: Attachment[],
    zipFileName: string,
    successMessage: string,
    logAction: () => void
  ): void {
    if (attachments.length === 0) {
      this.commonService.notify(
        'warning',
        'No attachments available to download'
      );
      return;
    }

    this.loaderService.show();
    this.studentAttachmentService
      .downloadAttachmentsAsZip(attachments, zipFileName)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', successMessage);
          logAction();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.logDownloadFailure(zipFileName, errorMessages);
        },
      });
  }

  private logDownloadFailure(
    zipFileName: string,
    errorMessages: string[]
  ): void {
    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;
    const logMessage = `Failed to download attachments (${zipFileName}) for ${studentName}. Errors: ${errorMessages.join(', ')}`;

    this.auditLogsService.logAudit({
      auditModule: AuditModule.CaseAttachment,
      auditOperationType: AuditOperationType.Downloaded,
      summary: logMessage,
      organizationIdOverride: this.caseParams.case?.student?.organizationId,
    });
  }

  downloadSelectedAttachments(item: AttachmentWithSelection): void {
    const selectedAttachments =
      item.folders?.flatMap(folder =>
        folder.attachments.filter((att: Attachment) => att.selected)
      ) || [];

    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;
    const zipFileName = `${studentName}__${item.title}_selected_${Date.now()}.zip`;

    this.downloadAttachments(
      selectedAttachments,
      zipFileName,
      'Selected attachments downloaded successfully',
      () => this.logDownloadAction('selected', item)
    );
  }

  downloadAllAttachments(section: AttachmentWithSelection): void {
    const allAttachments =
      section.folders?.flatMap(folder => folder.attachments) || [];

    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;
    const zipFileName = `${studentName}_${section.title}_all_${Date.now()}.zip`;

    this.downloadAttachments(
      allAttachments,
      zipFileName,
      'All attachments downloaded successfully',
      () => this.logDownloadAction('all', section)
    );
  }

  downloadFile(attachment: Attachment): void {
    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;

    try {
      window.open(attachment.fileUrl, '_blank');
      this.logDownloadSingleAction('success', attachment.name, studentName);
    } catch {
      this.logDownloadSingleAction('failure', attachment.name, studentName);
      this.commonService.notify(
        'error',
        'Failed to download the attachment. Please try again.'
      );
    }
  }

  private logDownloadSingleAction(
    status: 'success' | 'failure',
    fileName: string,
    studentName: string
  ): void {
    const logMessage =
      status === 'success'
        ? `Downloaded single attachment (${fileName}) for ${studentName}`
        : `Failed to download single attachment (${fileName}) for ${studentName}`;

    this.auditLogsService.logAudit({
      auditModule: AuditModule.CaseAttachment,
      auditOperationType: AuditOperationType.Downloaded,
      summary: logMessage,
      organizationIdOverride: this.caseParams.case?.student?.organizationId,
    });
  }

  trackByAttachmentId(index: number, attachment: Attachment): number {
    return attachment.id;
  }

  trackByFolderId(index: number, folder: SelectedFolder): number {
    return folder.folderId;
  }

  openAddAttachmentComponentDrawer(
    selectedPlan: AttachmentWithSelection
  ): void {
    this.drawerService
      .openDrawer(
        AddAttachmentComponent,
        this.addAttachmentDrawerTitleTpl || 'Add Attachment',
        520,
        true,
        undefined,
        {
          mode: this.atttachmentMode.CREATE,
          attachment: null,
          planTypeId: selectedPlan.planTypeId,
          case: this.caseParams.case,
          ...this.computeIsDraftQuestionnaire(selectedPlan.planTypeId),
        },
        DRAWER_IDS.STUDENT_ATTACHMENT
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadPlansWithAttachments();
      });
  }

  openAttemptQuestionnaireDrawer(
    selectedPlan: AttachmentWithSelection,
    attachment?: Attachment
  ): void {
    this.drawerService
      .openDrawer(
        QuestionnairesFormComponent,
        this.questionnairesDrawerTitleTpl || 'Attempt Questionnaire',
        1170,
        true,
        undefined,
        {
          mode: this.questionnaireMode.CREATE,
          planTypeId: selectedPlan.planTypeId,
          case: this.caseParams.case,
          attachment: attachment,
          ...this.computeIsDraftQuestionnaire(selectedPlan.planTypeId),
        },
        DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadPlansWithAttachments();
      });
  }

  viewAttachmentDetails(attachment: Attachment, planTypeId?: number): void {
    const resolvedPlanTypeId = planTypeId ?? 0;
    if (attachment.isQuestionnairePDF) {
      this.drawerService
        .openDrawer(
          QuestionnairesFormComponent,
          this.questionnairesDrawerTitleTpl || 'Attempt Questionnaire',
          1170,
          true,
          undefined,
          {
            mode: this.questionnaireMode.EDIT,
            planTypeId: resolvedPlanTypeId,
            case: this.caseParams.case,
            attachment: attachment,
            ...this.computeIsDraftQuestionnaire(resolvedPlanTypeId),
          },
          DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE
        )
        .afterClose.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loadPlansWithAttachments();
        });
      return;
    }

    this.drawerService
      .openDrawer(
        AddAttachmentComponent,
        'View Attachment',
        520,
        true,
        undefined,
        {
          attachment,
          mode: this.atttachmentMode.VIEW,
          planTypeId,
          case: this.caseParams.case,
          ...this.computeIsDraftQuestionnaire(resolvedPlanTypeId),
        },
        DRAWER_IDS.STUDENT_ATTACHMENT
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadPlansWithAttachments();
      });
  }

  closeAddAttachmentDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.STUDENT_ATTACHMENT);
  }

  closeQuestionnairesDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE);
  }

  getStudentFullName(): string {
    const firstName = this.caseParams.case?.student?.firstName ?? '';
    const lastName = this.caseParams.case?.student?.lastName ?? '';
    return `${firstName} ${lastName}`.trim();
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageStudentDrawerParams>(DRAWER_IDS.BIP_STUDENT_ATTACHMENT)
      .pipe(
        filter((params): params is ManageStudentDrawerParams => !!params),
        tap(params => {
          this.caseParams = params;
          this.folderForm = this.fb.group({
            caseId: this.caseParams?.case?.id,
            name: [
              null,
              [
                Validators.required,
                Validators.maxLength(50),
                Validators.pattern(this.titlePattern),
              ],
            ],
          });
        }),
        switchMap(params =>
          params.case?.bipTemplateId
            ? this.bipTemplateGatingService.getTemplate(
                params.case.bipTemplateId
              )
            : of(null)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(template => {
        this.bipTemplate = template;
      });
  }

  onDelete(id: number): void {
    this.showConfirmDialog = true;
    this.attachmentId = id;
  }

  handleDelete(): void {
    if (!this.attachmentId) return;

    this.isDeleting = true;
    this.studentAttachmentService
      .deleteAttachment(this.attachmentId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isDeleting = false;
          this.showConfirmDialog = false;
          this.attachmentId = null;
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'Attachment deleted successfully'
          );
          this.loadPlansWithAttachments();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.resetComponentState();
        },
      });
  }

  handleCancel(): void {
    this.showConfirmDialog = false;
    this.attachmentId = null;
  }

  onSaveFolder() {
    if (this.folderForm.invalid) {
      this.folderForm.get('name')?.markAsTouched();
      return;
    }

    const modifiedFields = this.auditContextService.buildFieldChanges(
      {},
      this.folderForm.value,
      {
        sensitiveFields: [],
        labelMap: {
          name: 'Folder Name',
        },
        excludeFields: ['caseId'],
      }
    );

    const payload = {
      caseId: this.folderForm.value.caseId,
      name: this.folderForm.value.name,
      _modifiedFields: modifiedFields,
    };
    this.isSavingFolder = true;
    this.folderSrv
      .createFolder(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSavingFolder = false;
        })
      )
      .subscribe({
        next: (res: ApiResponse<boolean>) => {
          if (res.result) {
            this.commonService.notify('success', 'Folder created successfully');
            this.isAddFolderOpen = false;
            this.folderForm.get('name')?.reset();
            // New (empty) folder now comes back from the API under every plan.
            this.loadPlansWithAttachments();
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify('error', errorMessages[0] || message);
          this.isSavingFolder = false;
        },
      });
  }

  onCancelFolder() {
    this.isAddFolderOpen = false;
    this.folderForm.get('name')?.reset();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
